import React, { useState } from 'react';

import classNames from 'classnames';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { RECRUITMENT_STATUS_PALETTE, recruitmentCategoryOptions } from './recruitmentStatusCategories';

import { apiClient } from 'Helpers/network';

import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import TextFormField from 'CommonComponents/TextFormField';
import ToggleFormField from 'CommonComponents/ToggleFormField';

import { useToast } from 'Hooks/useToast';

const firstErrors = (errors = {}) => Object.fromEntries(Object.entries(errors).map(([field, messages]) => [field, messages[0]]));

/**
 * Add or edit a recruitment status. Moving a status that positions use to another
 * category needs a confirmation: the server answers 409 and the form resends with
 * confirm_category_change once the user agrees.
 */
export default function RecruitmentStatusModal({ modalId, modalRef, status, storeUrl, updateUrl, onClose, onSuccess }) {
    const [pendingCategoryChange, setPendingCategoryChange] = useState(null);

    const toast = useToast();

    const validationSchema = Yup.object().shape({
        name: Yup.string().trim().required($t('This value is required')).max(100, $t('Must be 100 characters or fewer')),
        category: Yup.string().required($t('This value is required')),
        colour: Yup.string().required($t('This value is required')),
    });

    const close = () => {
        setPendingCategoryChange(null);
        onClose();
    };

    const save = (values, actions, confirmCategoryChange = false) => {
        const payload = {
            name: values.name.trim(),
            category: values.category,
            colour: values.colour,
            is_active: values.is_active,
            ...(confirmCategoryChange ? { confirm_category_change: true } : {}),
        };

        const request = status ? apiClient.put(updateUrl.replace('RECRUITMENT_STATUS_ID', status.id), payload) : apiClient.post(storeUrl, payload);

        request
            .then(() => {
                toast.success($t('Recruitment status saved'));
                setPendingCategoryChange(null);
                actions.resetForm();
                onSuccess();
            })
            .catch((err) => {
                if (err.response?.status === 409 && err.response.data?.needs_confirmation) {
                    setPendingCategoryChange({ values, actions, positionsCount: err.response.data.positions_count });

                    return;
                }

                if (err.response?.status === 422) {
                    actions.setErrors(firstErrors(err.response.data.errors));

                    return;
                }

                toast.error(err.response?.data?.message || $t('Something went wrong while saving the recruitment status'));
            })
            .finally(() => actions.setSubmitting(false));
    };

    return (
        <div id={modalId} ref={modalRef} tabIndex="-1" aria-hidden="true" className="hidden modal__box">
            <div className="modal__container">
                <div className="modal__inner-container">
                    <ModalHeader title={status ? $t('Edit Recruitment Status') : $t('Add Recruitment Status')} close={close} />
                    <div className="tw-rounded-b">
                        <div className="modal__form-container">
                            <Formik
                                initialValues={{
                                    name: status?.name ?? '',
                                    category: status?.category ?? recruitmentCategoryOptions()[0].value,
                                    colour: status?.colour ?? RECRUITMENT_STATUS_PALETTE[0],
                                    is_active: status?.is_active ?? true,
                                }}
                                enableReinitialize={true}
                                validationSchema={validationSchema}
                                onSubmit={(values, actions) => save(values, actions)}
                            >
                                {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => (
                                    <>
                                        {pendingCategoryChange ? (
                                            <div className="tw-flex tw-flex-col tw-gap-y-3 tw-text-sm">
                                                <p>
                                                    {$t(
                                                        '{{count}} positions use this status. Changing its category moves them to the new category on the dashboard and in reports.',
                                                        { count: pendingCategoryChange.positionsCount },
                                                    )}
                                                </p>
                                                <p className="tw-font-medium">{$t('Do you want to change the category?')}</p>
                                            </div>
                                        ) : (
                                            <div className="tw-flex tw-flex-col tw-gap-y-4">
                                                <div>
                                                    <TextFormField label={$t('Status name')} required={true} type="text" name="name" />
                                                    <p className="tw-mt-1 tw-text-xs tw-text-charcoal-75">
                                                        {$t('Shown in the position dropdown and on reports.')}
                                                    </p>
                                                </div>

                                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                    <label htmlFor="recruitment-status-category" className="text-input__label">
                                                        {$t('Category')} *
                                                    </label>
                                                    <select
                                                        id="recruitment-status-category"
                                                        className="select select-input !tw-bg-white !tw-border"
                                                        value={values.category}
                                                        onChange={(e) => setFieldValue('category', e.target.value)}
                                                    >
                                                        {recruitmentCategoryOptions().map((option) => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <p className="tw-text-xs tw-text-charcoal-75">{$t('Fixed category used for the dashboard and reports.')}</p>
                                                    {touched.category && errors.category && <div className="tw-text-red-500 tw-text-sm">{errors.category}</div>}
                                                </div>

                                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                    <span className="text-input__label">{$t('Colour')} *</span>
                                                    <div className="tw-flex tw-flex-wrap tw-gap-2" role="radiogroup" aria-label={$t('Colour')}>
                                                        {RECRUITMENT_STATUS_PALETTE.map((colour) => (
                                                            <button
                                                                key={colour}
                                                                type="button"
                                                                role="radio"
                                                                aria-checked={values.colour === colour}
                                                                aria-label={colour}
                                                                title={colour}
                                                                onClick={() => setFieldValue('colour', colour)}
                                                                className={classNames('tw-w-7 tw-h-7 tw-rounded-full tw-border-2', {
                                                                    'tw-border-charcoal tw-ring-2 tw-ring-offset-1 tw-ring-blue': values.colour === colour,
                                                                    'tw-border-white': values.colour !== colour,
                                                                })}
                                                                style={{ backgroundColor: colour }}
                                                            />
                                                        ))}
                                                    </div>
                                                    {errors.colour && <div className="tw-text-red-500 tw-text-sm">{errors.colour}</div>}
                                                </div>

                                                <div>
                                                    <ToggleFormField label={$t('Active')} type="checkbox" name="is_active" />
                                                    <p className="tw-text-xs tw-text-charcoal-75">
                                                        {$t('Inactive statuses stay on the positions that use them but are hidden from the dropdown.')}
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        <ModalFooter
                                            cancelButtonText={pendingCategoryChange ? $t('Back') : $t('Cancel')}
                                            primaryButtonText={pendingCategoryChange ? $t('Change category') : $t('Save')}
                                            submitButtonVariant="primary"
                                            onCancel={pendingCategoryChange ? () => setPendingCategoryChange(null) : close}
                                            handleSubmit={
                                                pendingCategoryChange
                                                    ? () => {
                                                          pendingCategoryChange.actions.setSubmitting(true);
                                                          save(pendingCategoryChange.values, pendingCategoryChange.actions, true);
                                                      }
                                                    : handleSubmit
                                            }
                                            isSubmitting={isSubmitting}
                                        />
                                    </>
                                )}
                            </Formik>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
